/**
 * Timezone utility for shared timezone selection across apps.
 * Provides IANA timezone data with friendly display names, localized country names,
 * and DST-aware abbreviations using native Intl APIs with a fallback map.
 */

export type TimezoneRegion = 'Americas' | 'Europe' | 'Middle East' | 'Africa' | 'Asia' | 'Pacific';

export interface TimezoneData {
    /** IANA timezone identifier (e.g., "America/New_York") */
    iana: string;
    /** City name (e.g., "New York") */
    city: string;
    /** Country name in English (e.g., "United States") - used as fallback */
    country: string;
    /** ISO 3166-1 alpha-2 country code (e.g., "US") - used for localization */
    countryCode?: string;
    /** Region for grouping (e.g., "Americas") */
    region: TimezoneRegion;
}

export interface FormattedTimezone extends TimezoneData {
    /** Display name (e.g., "New York, United States (EST)") */
    displayName: string;
    /** Timezone abbreviation (e.g., "EST", "AEST", "PST") */
    abbreviation: string;
    /** UTC offset in short format (e.g., "UTC-4", "UTC+5:30", "UTC+0") */
    offset: string;
    /** Current time in 12-hour format (e.g., "1:29 AM") */
    currentTime: string;
    /** Day/night icon: ☀️ (6 AM - 6 PM) or 🌘 (6 PM - 6 AM) */
    icon: '☀️' | '🌘';
}

/**
 * Abbreviation fallback keyed by IANA timezone ID.
 * Used when the browser's Intl API `timeZoneName: 'short'` returns "GMT+X"
 * instead of a named abbreviation.
 *
 * For DST-aware zones, stores [standard, daylight, standardOffset] so we can
 * pick the correct abbreviation by comparing the current UTC offset.
 */
const IANA_ABBREVIATIONS: Record<string, string | [string, string, number]> = {
    // Europe (DST-aware)
    'Europe/Lisbon': ['WET', 'WEST', 0],
    'Europe/Dublin': ['GMT', 'IST', 0],
    'Europe/London': ['GMT', 'BST', 0],
    'Europe/Amsterdam': ['CET', 'CEST', 1],
    'Europe/Brussels': ['CET', 'CEST', 1],
    'Europe/Paris': ['CET', 'CEST', 1],
    'Europe/Madrid': ['CET', 'CEST', 1],
    'Europe/Zurich': ['CET', 'CEST', 1],
    'Europe/Vienna': ['CET', 'CEST', 1],
    'Europe/Berlin': ['CET', 'CEST', 1],
    'Europe/Rome': ['CET', 'CEST', 1],
    'Europe/Copenhagen': ['CET', 'CEST', 1],
    'Europe/Oslo': ['CET', 'CEST', 1],
    'Europe/Stockholm': ['CET', 'CEST', 1],
    'Europe/Warsaw': ['CET', 'CEST', 1],
    'Europe/Prague': ['CET', 'CEST', 1],
    'Europe/Helsinki': ['EET', 'EEST', 2],
    'Europe/Athens': ['EET', 'EEST', 2],
    'Europe/Kiev': ['EET', 'EEST', 2],
    // Europe (no DST)
    'Europe/Moscow': 'MSK',
    'Europe/Istanbul': 'TRT',
    // Africa
    'Africa/Cairo': ['EET', 'EEST', 2],
    'Africa/Casablanca': 'WET',
    'Africa/Johannesburg': 'SAST',
    'Africa/Lagos': 'WAT',
    'Africa/Nairobi': 'EAT',
    'Africa/Accra': 'GMT',
    // Middle East
    'Asia/Dubai': 'GST',
    'Asia/Riyadh': 'AST',
    'Asia/Qatar': 'AST',
    'Asia/Jerusalem': ['IST', 'IDT', 2],
    'Asia/Tehran': ['IRST', 'IRDT', 3.5],
    // South Asia
    'Asia/Kolkata': 'IST',
    'Asia/Karachi': 'PKT',
    'Asia/Dhaka': 'BST',
    // Southeast Asia
    'Asia/Bangkok': 'ICT',
    'Asia/Ho_Chi_Minh': 'ICT',
    'Asia/Jakarta': 'WIB',
    'Asia/Singapore': 'SGT',
    'Asia/Kuala_Lumpur': 'MYT',
    'Asia/Manila': 'PHT',
    // East Asia
    'Asia/Hong_Kong': 'HKT',
    'Asia/Shanghai': 'CST',
    'Asia/Taipei': 'CST',
    'Asia/Seoul': 'KST',
    'Asia/Tokyo': 'JST',
    // Australia (DST-aware)
    'Australia/Sydney': ['AEST', 'AEDT', 10],
    'Australia/Melbourne': ['AEST', 'AEDT', 10],
    'Australia/Brisbane': 'AEST',
    'Australia/Adelaide': ['ACST', 'ACDT', 9.5],
    'Australia/Perth': 'AWST',
    // Pacific
    'Pacific/Auckland': ['NZST', 'NZDT', 12],
    'Pacific/Fiji': 'FJT',
    // Americas (not covered by short)
    'America/Bogota': 'COT',
    'America/Lima': 'PET',
    'America/Santiago': ['CLT', 'CLST', -4],
    'America/Buenos_Aires': 'ART',
    'America/Sao_Paulo': 'BRT',
    'America/St_Johns': ['NST', 'NDT', -3.5],
};

/**
 * Gets the numeric UTC offset for a timezone.
 * Used internally for DST detection in abbreviation fallback.
 */
function getNumericOffset(iana: string, referenceDate: Date = new Date()): number {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: iana,
            timeZoneName: 'shortOffset',
        }).formatToParts(referenceDate);
        const tzPart = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
        if (tzPart === 'GMT') return 0;
        const match = tzPart.match(/GMT([+-])(\d+)(?::(\d+))?/);
        if (!match) return 0;
        const sign = match[1] === '+' ? 1 : -1;
        const hours = parseInt(match[2], 10);
        const minutes = match[3] ? parseInt(match[3], 10) : 0;
        return sign * (hours + minutes / 60);
    } catch {
        return 0;
    }
}

/**
 * Curated list of commonly used IANA timezones grouped by region.
 * Covers major cities worldwide with unique offset/DST/abbreviation combinations.
 */
export const TIMEZONE_DATA: TimezoneData[] = [
    // Americas - United States
    {
        iana: 'America/New_York',
        city: 'New York',
        country: 'United States',
        countryCode: 'US',
        region: 'Americas',
    },
    {
        iana: 'America/Chicago',
        city: 'Chicago',
        country: 'United States',
        countryCode: 'US',
        region: 'Americas',
    },
    {
        iana: 'America/Denver',
        city: 'Denver',
        country: 'United States',
        countryCode: 'US',
        region: 'Americas',
    },
    {
        iana: 'America/Los_Angeles',
        city: 'Los Angeles',
        country: 'United States',
        countryCode: 'US',
        region: 'Americas',
    },
    {
        iana: 'America/Phoenix',
        city: 'Phoenix',
        country: 'United States',
        countryCode: 'US',
        region: 'Americas',
    },
    {
        iana: 'America/Anchorage',
        city: 'Anchorage',
        country: 'United States',
        countryCode: 'US',
        region: 'Americas',
    },
    {
        iana: 'Pacific/Honolulu',
        city: 'Honolulu',
        country: 'United States',
        countryCode: 'US',
        region: 'Americas',
    },
    // Americas - Canada
    {
        iana: 'America/Toronto',
        city: 'Toronto',
        country: 'Canada',
        countryCode: 'CA',
        region: 'Americas',
    },
    {
        iana: 'America/Vancouver',
        city: 'Vancouver',
        country: 'Canada',
        countryCode: 'CA',
        region: 'Americas',
    },
    {
        iana: 'America/Halifax',
        city: 'Halifax',
        country: 'Canada',
        countryCode: 'CA',
        region: 'Americas',
    },
    {
        iana: 'America/St_Johns',
        city: "St. John's",
        country: 'Canada',
        countryCode: 'CA',
        region: 'Americas',
    },
    // Americas - Mexico
    {
        iana: 'America/Mexico_City',
        city: 'Mexico City',
        country: 'Mexico',
        countryCode: 'MX',
        region: 'Americas',
    },
    {
        iana: 'America/Cancun',
        city: 'Cancún',
        country: 'Mexico',
        countryCode: 'MX',
        region: 'Americas',
    },
    // Americas - Central America & Caribbean
    {
        iana: 'America/Puerto_Rico',
        city: 'San Juan',
        country: 'Puerto Rico',
        countryCode: 'PR',
        region: 'Americas',
    },
    // Americas - South America
    {
        iana: 'America/Bogota',
        city: 'Bogota',
        country: 'Colombia',
        countryCode: 'CO',
        region: 'Americas',
    },
    { iana: 'America/Lima', city: 'Lima', country: 'Peru', countryCode: 'PE', region: 'Americas' },
    {
        iana: 'America/Santiago',
        city: 'Santiago',
        country: 'Chile',
        countryCode: 'CL',
        region: 'Americas',
    },
    {
        iana: 'America/Buenos_Aires',
        city: 'Buenos Aires',
        country: 'Argentina',
        countryCode: 'AR',
        region: 'Americas',
    },
    {
        iana: 'America/Sao_Paulo',
        city: 'São Paulo',
        country: 'Brazil',
        countryCode: 'BR',
        region: 'Americas',
    },

    // Europe
    {
        iana: 'Europe/London',
        city: 'London',
        country: 'United Kingdom',
        countryCode: 'GB',
        region: 'Europe',
    },
    {
        iana: 'Europe/Dublin',
        city: 'Dublin',
        country: 'Ireland',
        countryCode: 'IE',
        region: 'Europe',
    },
    { iana: 'Europe/Paris', city: 'Paris', country: 'France', countryCode: 'FR', region: 'Europe' },
    {
        iana: 'Europe/Berlin',
        city: 'Berlin',
        country: 'Germany',
        countryCode: 'DE',
        region: 'Europe',
    },
    {
        iana: 'Europe/Amsterdam',
        city: 'Amsterdam',
        country: 'Netherlands',
        countryCode: 'NL',
        region: 'Europe',
    },
    {
        iana: 'Europe/Brussels',
        city: 'Brussels',
        country: 'Belgium',
        countryCode: 'BE',
        region: 'Europe',
    },
    {
        iana: 'Europe/Zurich',
        city: 'Zurich',
        country: 'Switzerland',
        countryCode: 'CH',
        region: 'Europe',
    },
    {
        iana: 'Europe/Vienna',
        city: 'Vienna',
        country: 'Austria',
        countryCode: 'AT',
        region: 'Europe',
    },
    { iana: 'Europe/Rome', city: 'Rome', country: 'Italy', countryCode: 'IT', region: 'Europe' },
    {
        iana: 'Europe/Madrid',
        city: 'Madrid',
        country: 'Spain',
        countryCode: 'ES',
        region: 'Europe',
    },
    {
        iana: 'Europe/Lisbon',
        city: 'Lisbon',
        country: 'Portugal',
        countryCode: 'PT',
        region: 'Europe',
    },
    {
        iana: 'Europe/Stockholm',
        city: 'Stockholm',
        country: 'Sweden',
        countryCode: 'SE',
        region: 'Europe',
    },
    { iana: 'Europe/Oslo', city: 'Oslo', country: 'Norway', countryCode: 'NO', region: 'Europe' },
    {
        iana: 'Europe/Copenhagen',
        city: 'Copenhagen',
        country: 'Denmark',
        countryCode: 'DK',
        region: 'Europe',
    },
    {
        iana: 'Europe/Helsinki',
        city: 'Helsinki',
        country: 'Finland',
        countryCode: 'FI',
        region: 'Europe',
    },
    {
        iana: 'Europe/Warsaw',
        city: 'Warsaw',
        country: 'Poland',
        countryCode: 'PL',
        region: 'Europe',
    },
    {
        iana: 'Europe/Prague',
        city: 'Prague',
        country: 'Czech Republic',
        countryCode: 'CZ',
        region: 'Europe',
    },
    {
        iana: 'Europe/Athens',
        city: 'Athens',
        country: 'Greece',
        countryCode: 'GR',
        region: 'Europe',
    },
    {
        iana: 'Europe/Moscow',
        city: 'Moscow',
        country: 'Russia',
        countryCode: 'RU',
        region: 'Europe',
    },
    { iana: 'Europe/Kiev', city: 'Kyiv', country: 'Ukraine', countryCode: 'UA', region: 'Europe' },
    {
        iana: 'Europe/Istanbul',
        city: 'Istanbul',
        country: 'Turkey',
        countryCode: 'TR',
        region: 'Europe',
    },

    // Middle East
    {
        iana: 'Asia/Dubai',
        city: 'Dubai',
        country: 'United Arab Emirates',
        countryCode: 'AE',
        region: 'Middle East',
    },
    {
        iana: 'Asia/Riyadh',
        city: 'Riyadh',
        country: 'Saudi Arabia',
        countryCode: 'SA',
        region: 'Middle East',
    },
    {
        iana: 'Asia/Qatar',
        city: 'Doha',
        country: 'Qatar',
        countryCode: 'QA',
        region: 'Middle East',
    },
    {
        iana: 'Asia/Jerusalem',
        city: 'Jerusalem',
        country: 'Israel',
        countryCode: 'IL',
        region: 'Middle East',
    },
    {
        iana: 'Asia/Tehran',
        city: 'Tehran',
        country: 'Iran',
        countryCode: 'IR',
        region: 'Middle East',
    },

    // Africa
    { iana: 'Africa/Cairo', city: 'Cairo', country: 'Egypt', countryCode: 'EG', region: 'Africa' },
    {
        iana: 'Africa/Johannesburg',
        city: 'Johannesburg',
        country: 'South Africa',
        countryCode: 'ZA',
        region: 'Africa',
    },
    {
        iana: 'Africa/Lagos',
        city: 'Lagos',
        country: 'Nigeria',
        countryCode: 'NG',
        region: 'Africa',
    },
    {
        iana: 'Africa/Nairobi',
        city: 'Nairobi',
        country: 'Kenya',
        countryCode: 'KE',
        region: 'Africa',
    },
    {
        iana: 'Africa/Casablanca',
        city: 'Casablanca',
        country: 'Morocco',
        countryCode: 'MA',
        region: 'Africa',
    },
    { iana: 'Africa/Accra', city: 'Accra', country: 'Ghana', countryCode: 'GH', region: 'Africa' },

    // Asia
    { iana: 'Asia/Kolkata', city: 'Mumbai', country: 'India', countryCode: 'IN', region: 'Asia' },
    {
        iana: 'Asia/Karachi',
        city: 'Karachi',
        country: 'Pakistan',
        countryCode: 'PK',
        region: 'Asia',
    },
    { iana: 'Asia/Dhaka', city: 'Dhaka', country: 'Bangladesh', countryCode: 'BD', region: 'Asia' },
    {
        iana: 'Asia/Bangkok',
        city: 'Bangkok',
        country: 'Thailand',
        countryCode: 'TH',
        region: 'Asia',
    },
    {
        iana: 'Asia/Ho_Chi_Minh',
        city: 'Ho Chi Minh City',
        country: 'Vietnam',
        countryCode: 'VN',
        region: 'Asia',
    },
    {
        iana: 'Asia/Singapore',
        city: 'Singapore',
        country: 'Singapore',
        countryCode: 'SG',
        region: 'Asia',
    },
    {
        iana: 'Asia/Kuala_Lumpur',
        city: 'Kuala Lumpur',
        country: 'Malaysia',
        countryCode: 'MY',
        region: 'Asia',
    },
    {
        iana: 'Asia/Jakarta',
        city: 'Jakarta',
        country: 'Indonesia',
        countryCode: 'ID',
        region: 'Asia',
    },
    {
        iana: 'Asia/Manila',
        city: 'Manila',
        country: 'Philippines',
        countryCode: 'PH',
        region: 'Asia',
    },
    {
        iana: 'Asia/Hong_Kong',
        city: 'Hong Kong',
        country: 'Hong Kong',
        countryCode: 'HK',
        region: 'Asia',
    },
    {
        iana: 'Asia/Shanghai',
        city: 'Shanghai',
        country: 'China',
        countryCode: 'CN',
        region: 'Asia',
    },
    { iana: 'Asia/Taipei', city: 'Taipei', country: 'Taiwan', countryCode: 'TW', region: 'Asia' },
    {
        iana: 'Asia/Seoul',
        city: 'Seoul',
        country: 'South Korea',
        countryCode: 'KR',
        region: 'Asia',
    },
    { iana: 'Asia/Tokyo', city: 'Tokyo', country: 'Japan', countryCode: 'JP', region: 'Asia' },

    // Pacific - Australia
    {
        iana: 'Australia/Sydney',
        city: 'Sydney',
        country: 'Australia',
        countryCode: 'AU',
        region: 'Pacific',
    },
    {
        iana: 'Australia/Melbourne',
        city: 'Melbourne',
        country: 'Australia',
        countryCode: 'AU',
        region: 'Pacific',
    },
    {
        iana: 'Australia/Brisbane',
        city: 'Brisbane',
        country: 'Australia',
        countryCode: 'AU',
        region: 'Pacific',
    },
    {
        iana: 'Australia/Perth',
        city: 'Perth',
        country: 'Australia',
        countryCode: 'AU',
        region: 'Pacific',
    },
    {
        iana: 'Australia/Adelaide',
        city: 'Adelaide',
        country: 'Australia',
        countryCode: 'AU',
        region: 'Pacific',
    },
    // Pacific - Other
    {
        iana: 'Pacific/Auckland',
        city: 'Auckland',
        country: 'New Zealand',
        countryCode: 'NZ',
        region: 'Pacific',
    },
    { iana: 'Pacific/Fiji', city: 'Suva', country: 'Fiji', countryCode: 'FJ', region: 'Pacific' },
];

/** Region display order for the dropdown sections. */
export const REGION_ORDER: TimezoneRegion[] = [
    'Americas',
    'Europe',
    'Middle East',
    'Africa',
    'Asia',
    'Pacific',
];

/**
 * Gets localized country name using Intl.DisplayNames.
 * Falls back to English country name if localization fails or countryCode is missing.
 */
function getLocalizedCountryName(
    countryCode: string | undefined,
    englishName: string,
    locale = 'en-US'
): string {
    if (!countryCode) return englishName;
    try {
        const displayNames = new Intl.DisplayNames([locale], { type: 'region' });
        return displayNames.of(countryCode) ?? englishName;
    } catch {
        return englishName;
    }
}

/**
 * Gets the timezone abbreviation (e.g., "EST", "AEST", "PST").
 * Uses a 2-tier strategy:
 * 1. Try Intl 'short' format — works for US/Canada/Mexico zones
 * 2. Look up IANA_ABBREVIATIONS map for zones where browsers return "GMT+X"
 *
 * @param iana - IANA timezone identifier
 * @param referenceDate - Date to calculate abbreviation for (defaults to now)
 * @returns Timezone abbreviation (e.g., "EST", "AEST", "PST")
 */
export function getTimezoneAbbreviation(iana: string, referenceDate: Date = new Date()): string {
    try {
        // Tier 1: Try short format — works for US/Canada/Mexico zones
        const shortParts = new Intl.DateTimeFormat('en-US', {
            timeZone: iana,
            timeZoneName: 'short',
        }).formatToParts(referenceDate);
        const shortValue = shortParts.find((p) => p.type === 'timeZoneName')?.value ?? '';

        // If Intl returned a proper abbreviation (not "GMT+X"), use it
        if (shortValue && !shortValue.startsWith('GMT')) {
            return shortValue;
        }

        // Tier 2: Direct IANA key lookup with DST awareness
        const fallback = IANA_ABBREVIATIONS[iana];
        if (fallback) {
            if (typeof fallback === 'string') {
                return fallback;
            }
            const [standard, daylight, stdOffset] = fallback;
            const currentOffset = getNumericOffset(iana, referenceDate);
            return currentOffset === stdOffset ? standard : daylight;
        }

        // Final fallback: return whatever Intl gave us (e.g. "GMT+5:30")
        return shortValue;
    } catch {
        return '';
    }
}

/**
 * Gets the UTC offset for a timezone in short format.
 *
 * @param iana - IANA timezone identifier
 * @param referenceDate - Date to calculate offset for (defaults to now)
 * @returns UTC offset string (e.g., "UTC-4", "UTC+5:30", "UTC+0")
 */
function getTimezoneOffset(iana: string, referenceDate: Date = new Date()): string {
    try {
        const parts = new Intl.DateTimeFormat('en-US', {
            timeZone: iana,
            timeZoneName: 'shortOffset',
        }).formatToParts(referenceDate);
        const tzPart = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';

        if (tzPart === 'GMT' || tzPart === 'GMT+00' || tzPart === 'GMT-00') {
            return 'UTC+0';
        }

        let offset = tzPart.replace('GMT', 'UTC');
        // Remove leading zeros from hours (e.g., "UTC+05" -> "UTC+5")
        offset = offset.replace(/UTC([+-])0(\d)/, 'UTC$1$2');
        return offset;
    } catch {
        return 'UTC';
    }
}

/**
 * Gets the current time in a timezone formatted in 12-hour format.
 *
 * @param iana - IANA timezone identifier
 * @param referenceDate - Date to format (defaults to now)
 * @returns Formatted time string (e.g., "1:29 AM")
 */
function getTimezoneCurrentTime(iana: string, referenceDate: Date = new Date()): string {
    try {
        return new Intl.DateTimeFormat('en-US', {
            timeZone: iana,
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        }).format(referenceDate);
    } catch {
        return '';
    }
}

/**
 * Gets the day/night icon for a timezone based on current time.
 * ☀️ for 6 AM - 5:59 PM (daytime)
 * 🌘 for 6 PM - 5:59 AM (nighttime)
 *
 * @param iana - IANA timezone identifier
 * @param referenceDate - Date to check (defaults to now)
 * @returns Day or night emoji icon
 */
export function getTimeOfDayIcon(iana: string, referenceDate: Date = new Date()): '☀️' | '🌘' {
    try {
        const hourStr = new Intl.DateTimeFormat('en-US', {
            timeZone: iana,
            hour: 'numeric',
            hour12: false,
        }).format(referenceDate);
        const hour = parseInt(hourStr, 10);
        return hour >= 6 && hour < 18 ? '☀️' : '🌘';
    } catch {
        return '☀️';
    }
}

/**
 * Formats a timezone data object into a FormattedTimezone with display name.
 * Display format: "City, Country (ABBR)" e.g. "New York, United States (EST)"
 *
 * @param tz - Timezone data object
 * @param referenceDate - Date to use for abbreviation calculation (defaults to now)
 * @param locale - BCP 47 locale string for country name localization (defaults to 'en-US')
 * @returns Formatted timezone with displayName and abbreviation
 */
export function formatTimezone(
    tz: TimezoneData,
    referenceDate: Date = new Date(),
    locale = 'en-US'
): FormattedTimezone {
    const abbreviation = getTimezoneAbbreviation(tz.iana, referenceDate);
    const country = getLocalizedCountryName(tz.countryCode, tz.country, locale);
    const suffix = abbreviation ? ` (${abbreviation})` : '';
    const displayName = `${tz.city}, ${country}${suffix}`;

    return {
        ...tz,
        displayName,
        abbreviation,
        offset: getTimezoneOffset(tz.iana, referenceDate),
        currentTime: getTimezoneCurrentTime(tz.iana, referenceDate),
        icon: getTimeOfDayIcon(tz.iana, referenceDate),
    };
}

/**
 * Gets all timezones grouped by region, formatted with display names.
 *
 * @param referenceDate - Date to use for abbreviation calculation (defaults to now)
 * @param locale - BCP 47 locale string for country name localization (defaults to 'en-US')
 * @returns Map of region to formatted timezones, ordered by REGION_ORDER
 */
export function getFormattedTimezonesByRegion(
    referenceDate: Date = new Date(),
    locale = 'en-US'
): Map<TimezoneRegion, FormattedTimezone[]> {
    const regionMap = new Map<TimezoneRegion, FormattedTimezone[]>();

    for (const region of REGION_ORDER) {
        regionMap.set(region, []);
    }

    for (const tz of TIMEZONE_DATA) {
        const formatted = formatTimezone(tz, referenceDate, locale);
        const timezones = regionMap.get(tz.region);
        if (timezones) {
            timezones.push(formatted);
        }
    }

    return regionMap;
}

/**
 * Finds a timezone by IANA identifier and returns its formatted version.
 * Falls back to creating a display name from the IANA identifier if not found
 * in the curated list.
 *
 * @param iana - IANA timezone identifier
 * @param referenceDate - Date to use for abbreviation calculation (defaults to now)
 * @param locale - BCP 47 locale string for country name localization (defaults to 'en-US')
 * @returns Formatted timezone (always returns a value)
 */
export function getOrCreateFormattedTimezone(
    iana: string,
    referenceDate: Date = new Date(),
    locale = 'en-US'
): FormattedTimezone {
    const existing = TIMEZONE_DATA.find((tz) => tz.iana === iana);
    if (existing) {
        return formatTimezone(existing, referenceDate, locale);
    }

    // Fallback for timezones not in the curated list — show raw IANA identifier
    const abbreviation = getTimezoneAbbreviation(iana, referenceDate);
    const suffix = abbreviation ? ` (${abbreviation})` : '';

    return {
        iana,
        city: iana,
        country: '',
        region: iana.split('/')[0] as TimezoneRegion,
        displayName: `${iana}${suffix}`,
        abbreviation,
        offset: getTimezoneOffset(iana, referenceDate),
        currentTime: getTimezoneCurrentTime(iana, referenceDate),
        icon: getTimeOfDayIcon(iana, referenceDate),
    };
}
